import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { updateUserWorkStatusApis } from "../redux/users/userActions";
import { getAttendanceDashboard, getWorkingHours, getStatusClock, } from "../redux/dashboard/dashboardActions";
import { fetchActiveTasksApi } from "../redux/dashboard/dashboardApi";

const statusOptions = [
  {
    value: "Active",
    label: "Active",
    subtitle: "Based on chat & activity",
    color: "text-success",
  },
  {
    value: "Break",
    label: "Break",
    subtitle: "Taking a short break",
    color: "text-warning",
  },
  {
    value: "Lunch",
    label: "Lunch",
    subtitle: "Out for lunch",
    color: "text-danger",
  },
  {
    value: "Be right back",
    label: "Be right back",
    subtitle: "Stepping away momentarily",
    color: "text-warning",
  },
  {
    /* Signing off, not pausing. Clocks you out and holds the running task the
       same way a break does — the difference is that it is not coming back
       today, so it carries no stopwatch and is not counted as break time.

       It exists because the alternative people actually use is closing the
       tab, which leaves the task In Progress overnight and turns the next
       day's report into an eleven hour stretch that has to be explained. */
    value: "End of day",
    label: "End of day",
    subtitle: "Signing off — clocks you out",
    color: "text-secondary",
    endsDay: true,
  },
  // {
  //   value: "Vacation",
  //   label: "Vacation",
  //   subtitle: "Currently on leave",
  //   color: "text-secondary",
  // },
];

/* The statuses that mean "stepping away and coming back". Break, Lunch and Be
   right back all clock you out, so all three are time away and all three
   deserve a clock.

   End of day is deliberately NOT here. It clocks you out like the rest, but a
   stopwatch counting up from the moment somebody went home is not a break
   timer, it is a number that reads 14:32:07 the next morning. */
const BREAK_STATUSES = ["Break", "Lunch", "Be right back"];
const isBreak = (s) => BREAK_STATUSES.includes(s);

/* Persisted so the count survives a refresh. Keyed per user: two accounts on
   one machine must not inherit each other's break.

   Anchored to the server's punch time (autoPunch.at) whenever there is one,
   because the whole point of the number is that it matches the attendance
   record. The browser clock is the fallback for the case where the status
   changed but no punch was recorded — picking "Be right back" while Lunch has
   already clocked you out. */
const breakKey = (userId) => `breakSince:${userId ?? "anon"}`;

const readBreakSince = (userId) => {
  const raw = localStorage.getItem(breakKey(userId));
  const n = Number(raw);
  return raw && Number.isFinite(n) && n > 0 ? n : null;
};

const elapsedHHMMSS = (fromMs) => {
  const secs = Math.max(0, Math.floor((Date.now() - fromMs) / 1000));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    : `${m}:${String(s).padStart(2, "0")}`;
};

const WorkStatusDropdown = ({ currentUser, onStatusChanged }) => {
  const dispatch = useDispatch();

  /* The live value, from tbl_User via /dashboard/clock-status.
   *
   * currentUser is the object written to localStorage at login. It is a
   * snapshot: picking Lunch updated the database and that snapshot kept
   * saying whatever it said at sign-in, so the menu read "Active" no matter
   * what — on this tab and on every future one.
   *
   * The old default made it worse. "ClockedOut" is not one of statusOptions,
   * so the lookup below fell through to statusOptions[0] and rendered the
   * word "Active" with a green dot. A status the server had never agreed to,
   * displayed with total confidence. */
  const serverStatus = useSelector((s) => s.dashboard.workStatus);

  /* Only Active is offered while this person has a task In Progress.
   *
   * REINSTATED ON REQUEST, 22 Aug. This menu has now been through four shapes:
   * options hidden, options disabled, everything pickable, and hidden again.
   * The argument for leaving it open is recorded here because it has not gone
   * away and whoever reads this next deserves it:
   *
   *   A block refuses the honest signal. Somebody who wants to say "I am at
   *   lunch" and is told no walks away with the task running anyway — the
   *   exact outcome the block exists to prevent, minus the record of where
   *   they went. And the server already handles it: choosing a break holds
   *   whatever is running (holdActiveTasksForBreak, applyWorkStatusToTasks,
   *   sp_User_UpdateWorkStatus, each in its own layer), so nothing is lost by
   *   letting the status through and saying what it did.
   *
   * The counter-argument, and the reason it is back: a task left In Progress
   * across a break bills the break as working time, and stopping it at the
   * moment somebody leaves is more accurate than holding it after the fact.
   *
   * Whichever wins, it is one line — `visibleOptions` below. */

  const [currentStatus, setCurrentStatus] = useState(
    currentUser?.WorkStatus || null,
  );


  // 1. Add state to control if the dropdown is open or closed
  const [isOpen, setIsOpen] = useState(false);

  /* How long this break has been running. `tick` exists only to re-render once
     a second — the elapsed value is derived from breakSince, so the display
     cannot drift away from the anchor the way an incrementing counter would. */
  const [breakSince, setBreakSince] = useState(() =>
    isBreak(currentUser?.WorkStatus) ? readBreakSince(currentUser?.userId) : null,
  );
  const [, setTick] = useState(0);

  /* Keep the anchor in step with the status, wherever the status came from —
     this menu, another tab, or a fresh page load. */
  useEffect(() => {
    if (!isBreak(currentStatus)) {
      localStorage.removeItem(breakKey(currentUser?.userId));
      setBreakSince(null);
      return;
    }
    const stored = readBreakSince(currentUser?.userId);
    if (stored) {
      setBreakSince(stored);
    } else {
      // On a break with no anchor — started elsewhere, or before this shipped.
      // Start counting from now rather than showing nothing.
      const now = Date.now();
      localStorage.setItem(breakKey(currentUser?.userId), String(now));
      setBreakSince(now);
    }
  }, [currentStatus, currentUser?.userId]);

  useEffect(() => {
    if (!breakSince) return undefined;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [breakSince]);

  // 2. Add a ref to detect clicks outside the dropdown
  /* Whether THIS person has a task open right now.
   *
   * Fetched straight from the API rather than read from the dashboard's
   * activeTasks slice, for two reasons. That slice is the TEAM feed, which
   * strips super admins — so an administrator with a task In Progress looked
   * idle to this menu and was offered Lunch. And writing a `scope=me` result
   * into the shared slice would empty the Working On Now panel next to it.
   *
   * Read when the menu opens, which is the only moment the answer matters. */
  const [runningTask, setRunningTask] = useState(null);

  const refreshRunningTask = async () => {
    try {
      const res = await fetchActiveTasksApi({ scope: "me" });
      const body = res?.data ?? res;
      const inner = body?.data ?? body;
      const list = Array.isArray(inner) ? inner : [];

      /* Filtered HERE as well as by the server.
       *
       * scope=me is a request, not a guarantee: an API build that predates it
       * ignores the parameter and answers with the whole team. Taking the
       * first row of that would block this person's status because somebody
       * else had a task open — the menu would be right by accident on a
       * one-row day and wrong on every other.
       *
       * The user id is the only thing that decides it, and this side knows
       * who is signed in. */
      const me = Number(currentUser?.userId);
      const mine = me
        ? list.filter(
            (t) =>
              Number(t.AssigneeCode) === me || Number(t.UserCode) === me,
          )
        : [];

      setRunningTask(mine[0] ?? null);
    } catch {
      /* Unknown is treated as "nothing running": a menu that refuses every
         option because a lookup failed is worse than one that lets somebody
         take a break the server will handle anyway. */
      setRunningTask(null);
    }
  };

  const dropdownRef = useRef(null);

  // useEffect(() => {
  //   const workStatus = localStorage.getItem("workStatus")
  //   setCurrentStatus(workStatus);
  // }, []);

  /* Ask the server what this account's status actually is, once on mount.
     Nothing else did, which is the whole bug: the menu could only ever show
     what happened to be in localStorage from sign-in. */
  useEffect(() => {
    dispatch(getStatusClock());
  }, [dispatch]);

  /* The server wins whenever it answers — including when the change came from
     another tab or was set on a different machine. Not applied while the menu
     is open: overwriting the selection under the reader's cursor mid-click is
     worse than being a second stale. */
  useEffect(() => {
    if (serverStatus && !isOpen) setCurrentStatus(serverStatus);
  }, [serverStatus, isOpen]);

  // 3. Handle clicking outside to close it automatically
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

    /* Everything except Active is hidden while a task is running.
   *
   * Not a list of the four to hide: Break, Lunch, Be right back and End of
   * day all step away from the desk, and naming them individually is how End
   * of day came to be added later and slip through. The rule is the other way
   * round — with a task open, the only thing you can be is Active. Stop the
   * task and the rest come back. */
  const visibleOptions = useMemo(
    () =>
      runningTask
        ? statusOptions.filter((o) => o.value === "Active")
        : statusOptions,
    [runningTask],
  );

  const handleStatusChange = async (newStatus) => {
    /* Refused as well as hidden. The menu is one way in; this is the door. */
    if (runningTask && newStatus !== "Active") {
      toast.warning(
        `Stop "${runningTask.TaskName || runningTask.NatureofQuery}" (#${
          runningTask.QueryNo
        }) before changing your status.`,
      );
      setIsOpen(false);
      return;
    }

      // if (!currentUser?.IsClockedIn) {
      //   toast.warning("Please Clock In first.");
      //   return;
      // }
      if (currentStatus === newStatus) return;

      const previousStatus = currentStatus;

      // localStorage.setItem("workStatus", newStatus)
      setCurrentStatus(newStatus);
      setIsOpen(false); // Close the dropdown after picking a status

      try {
        const payload = {
          id: currentUser?.userId,
          workStatus: newStatus,
        };
        console.log("Updating work status", payload);

        const result = await dispatch(updateUserWorkStatusApis(payload)).unwrap();
        console.log("Status updated successfully", result);

        // setCurrentStatus(newStatus);
        // setIsOpen(false);

        onStatusChanged?.(newStatus);

        // The API moves the clock with the status — Lunch / Break / Be right
        // back clock out, Active clocks back in — and reports what it did.
        // Saying so matters: choosing "Be right back" when Lunch already
        // clocked you out records nothing, and without a word for it that reads
        // as the feature being broken.
        const autoPunch = result?.data?.autoPunch;

        /* Anchor the break clock to the punch the server just wrote, so the
           number on screen and the attendance record agree. Only when the
           punch actually happened: an "ALREADY" outcome means the break began
           earlier, and re-anchoring would restart a clock that should keep
           running. */
        if (isBreak(newStatus) && autoPunch?.applied && autoPunch.at) {
          const at = new Date(autoPunch.at).getTime();
          if (Number.isFinite(at)) {
            localStorage.setItem(breakKey(currentUser?.userId), String(at));
            setBreakSince(at);
          }
        }

        /* A refused punch has to be said out loud.
         *
         * The status write and the punch are separate, and only the first of
         * them can fail quietly: tbl_User gets the new status either way, so
         * picking Active always LOOKED like it worked. When the punch was
         * refused — the daily cap, most often — the person was left green and
         * Active beside a header reading "Clock Out", with nothing anywhere
         * saying why, and nothing they could press to fix it. It reads as the
         * header being broken, which is how it was reported.
         *
         * Every outcome that is not a punch now has a sentence. */
        const direction = autoPunch?.punchType === "IN" ? "in" : "out";

        if (autoPunch?.applied) {
          toast.success(
            `Status updated to ${newStatus}. You have been clocked ${direction}.`
          );
        } else if (autoPunch?.outcome === "ALREADY") {
          toast.info(
            `Status updated to ${newStatus}. You were already clocked ${direction}.`
          );
        } else if (autoPunch?.outcome === "DAY_LIMIT") {
          toast.warning(
            `Status updated to ${newStatus}, but you could not be clocked ${direction} — you have hit today's punch limit. Please raise a regularization request.`
          );
        } else if (autoPunch?.outcome) {
          toast.warning(
            `Status updated to ${newStatus}, but you could not be clocked ${direction}.`
          );
        } else {
          toast.success(`Status updated to ${newStatus}`);
        }

        /* Stepping away holds whatever was running, and that is the one thing
           here the person did not press. This line is what the menu now uses
           instead of standing in their way: they get to go to lunch, and they
           get told the task was paused. Without it they would find it On Hold
           tomorrow with no memory of doing it.

           Its own toast rather than being folded into the status message
           above, because the status message already varies four ways on what
           the clock did. */
        const heldCount =
          result?.data?.taskMove?.changed?.length ||
          result?.data?.heldTasks?.length ||
          0;

        if (heldCount) {
          toast.info(
            `${heldCount} task${heldCount > 1 ? "s were" : " was"} put on hold.`,
          );
        }

        // getStatusClock drives the header Clock In / Clock Out button. Without
        // refreshing it the button kept its old label after an auto punch, so a
        // clock-out that had actually happened still showed "Clock Out".
        await Promise.all([
          dispatch(getAttendanceDashboard()).unwrap(),
          dispatch(getWorkingHours(currentUser.userId)).unwrap(),
          dispatch(getStatusClock()).unwrap(),
        ]);

      } catch (error) {
        toast.error("Failed to update status. Reverting...");
        setCurrentStatus(previousStatus);

        toast.error(error?.message || "Failed to update status")
      }
    };

  /* No silent fallback to statusOptions[0]. An unrecognised or not-yet-loaded
     status now reads as "…" rather than claiming Active — which is exactly how
     a value the server had never sent came to be shown as though it had. */
  const activeStatusObj = statusOptions.find((s) => s.value === currentStatus) || {
    value: currentStatus || "",
    label: currentStatus || "…",
    color: "text-secondary",
  };

  return (
    // Attach the ref here
    <div className="dropdown position-relative" ref={dropdownRef}>
      <button
        className="btn btn-light d-flex align-items-center gap-2 border shadow-sm rounded-pill px-3 py-1 bg-white"
        type="button"
        // 4. Toggle the dropdown state on click
        onClick={() => {
          /* Re-read before opening: this header is on every page, and a task
             started a minute ago on another screen must still close the menu
             down. */
          if (!isOpen) refreshRunningTask();
          setIsOpen(!isOpen);
        }}
        style={{ cursor: "pointer", transition: "all 0.2s" }}
      >
        <i
          className={`bi bi-circle-fill ${activeStatusObj.color}`}
          style={{ fontSize: "0.6rem" }}
        ></i>
        <span
          className="fw-medium d-none d-md-block"
          style={{ fontSize: "0.9rem" }}
        >
          {activeStatusObj.label}
        </span>

        {/* The break clock. On the button rather than inside the menu: the
            point of it is to be visible without being asked for — a lunch that
            has quietly run to 1:20 is the thing you want to notice. */}
        {breakSince && (
          <span
            className="d-inline-flex align-items-center gap-1 fw-bold"
            style={{
              fontSize: "0.78rem",
              color: "#e65100",
              background: "#fff3e0",
              borderRadius: "20px",
              padding: "1px 9px",
              fontVariantNumeric: "tabular-nums",
            }}
            title="Time on this break"
          >
            <i className="bi bi-stopwatch" style={{ fontSize: "0.7rem" }} />
            {elapsedHHMMSS(breakSince)}
          </span>
        )}

        <i
          className="bi bi-chevron-down ms-1 text-muted"
          style={{ fontSize: "0.7rem" }}
        ></i>
      </button>

      {/* 5. Conditionally add the "show" class and display style based on isOpen state */}
      <ul
        className={`dropdown-menu shadow border-0 mt-2 p-2 ${isOpen ? "show" : ""
          }`}
        style={{
          width: window.innerWidth <= 768 ? "220px" : "260px",
          borderRadius: "10px",
          position: "absolute",
          top: "calc(100% + 8px)",
          left: window.innerWidth <= 768 ? "0" : "auto",
          right: window.innerWidth <= 768 ? "auto" : "0",
          display: isOpen ? "block" : "none",
          zIndex: 3000,
        }}
      >
        {/* Every option, always enabled, for every user. Nothing here is
            conditional on what the person has open — see the note above
            handleStatusChange. */}
        {visibleOptions.map((opt) => (
          <li key={opt.value}>
            {/* End of day is a different kind of act from the three above it —
                it closes the day rather than pausing it — so it is set apart
                rather than sitting in the list as a fourth break. */}
            {opt.endsDay && <hr className="my-1 opacity-25" />}

            <button
              className={`dropdown-item d-flex align-items-start gap-3 py-2 px-3 rounded ${currentStatus === opt.value ? "bg-light" : ""
                }`}
              onClick={() => handleStatusChange(opt.value)}
              style={{ cursor: "pointer" }}
            >
              <div className="mt-1">
                <i
                  className={`bi bi-circle-fill ${opt.color}`}
                  style={{ fontSize: "0.7rem" }}
                ></i>
              </div>

              <div className="d-flex flex-column flex-grow-1 text-start">
                <span
                  className="fw-medium text-dark"
                  style={{ fontSize: "0.9rem" }}
                >
                  {opt.label}
                </span>
                <span
                  className="text-muted"
                  style={{
                    fontSize: "0.75rem",
                    whiteSpace: "normal",
                    lineHeight: 1.25,
                  }}
                >
                  {opt.subtitle}
                </span>
              </div>

              {currentStatus === opt.value && (
                <div className="ms-auto mt-1">
                  <i className="bi bi-check2 text-dark fs-5"></i>
                </div>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default WorkStatusDropdown;